import { get, post } from "lib/api";
import {
	ErrorResponseType,
	copyToClipboard,
	sortByOrder,
} from "lib/utils/common";
import {
	QuestionType,
	UserMetaDataType,
	transformQuestion,
} from "lib/utils/mpt";
import getConfig from "next/config";
import {
	SurveyType,
	CohortData,
	SurveyMasterType,
	SurveyStartedEventResponseType,
	QuestionAnswerType,
	OtherCohortsType,
	MPTResultType,
	SurveyCohortType,
	SubmitSurveyResponseType,
	QuestionOrderResponseType,
} from "../types/personality";
import { sendToApp } from "./intercom";
import { captureException } from "@sentry/nextjs";
import { UserType } from "../types/user";
import { EventObject, assign, createMachine } from "xstate";
import { AppEvent, trackEvent } from "./events";
import { clone } from "ramda";

const { publicRuntimeConfig } = getConfig();
const { BASE_URL, API_URL } = publicRuntimeConfig;

export function getLatestSurveys(): Promise<Array<SurveyType>> {
	return get(`${API_URL}/api-gateway/mpt/v1/surveys?latest=true`).then(
		(resp: any) => (resp ? resp : [])
	);
}

export function getLatestCohorts(): Promise<Array<CohortData>> {
	return get(`${BASE_URL}/api/v1/surveys/cohorts?latest=true`).then(
		(resp: any) => (resp ? resp : [])
	);
}

export function getSurvey(
	id: number,
	version?: string
): Promise<SurveyMasterType> {
	return get(
		`${API_URL}/api-gateway/mpt/v1/surveys/${id}${
			version ? `?version=${version}` : ""
		}`
	);
}

export function getSurveyCohorts(
	id: number
): Promise<{ cohorts: SurveyCohortType[] }> {
	return get(`${API_URL}/api-gateway/mpt/v1/surveys/${id}/cohorts`);
}

export function publishSurveyStartedEvent(data: {
	survey_id: number;
	survey_type: number;
	version_identifier?: string | undefined;
}): Promise<SurveyStartedEventResponseType> {
	return post(`${BASE_URL}/api/v1/surveys/events/start`, data);
}

export function updateSurveyMetadata(metadata: {
	user_data: UserMetaDataType;
}): Promise<{}> {
	return post(`${BASE_URL}/api/v1/surveys/metadata`, metadata);
}

export function submitSurvey(
	id: number,
	data: {
		metadata: {
			user_data: UserMetaDataType;
			shared_code?: string;
			version_identifier?: string | undefined;
		};
		responses: Array<QuestionAnswerType>;
		survey_id: number;
		survey_type: number;
		test_instance_identifier: string;
	}
): Promise<SubmitSurveyResponseType> {
	return post(`${BASE_URL}/api-gateway/mpt/v2/submit/${id}`, data);
}

export function getResults(id: string, headers?: any): Promise<MPTResultType> {
	return get(`${API_URL}/api-gateway/mpt/v1/results/${id}`, { headers });
}

// export const useGetMptResult = ({
// 	id,
// 	onError,
// 	onSuccess
// }: {
// 	id: string,
// 	onSuccess?: (result: MPTResultType) => void,
// 	onError?: (err: any) => void,
// }) => {
// 	return useQuery(["etMptResult"], (id: string) => getResults(id), {
// 		onSuccess,
// 		onError,
// 		on
// 	})
// }

/**
 * This api returns the mpt result (limited result data) without authentication
 * @param id
 * @param headers
 * @returns
 */
export function getSUDMptResults(
	id: string,
	headers?: any
): Promise<MPTResultType> {
	return get(`${API_URL}/api-gateway/mpt/v1/external/sud/results/${id}`, {
		headers,
	});
}

/**
 * This api returns the mpt result (limited result data) without authentication
 * @param id
 * @param headers
 * @returns
 */
export function getGlobalMptResults(
	id: string,
	headers?: any
): Promise<MPTResultType> {
	return get(`${API_URL}/api-gateway/mpt/v1/global/results/${id}`, {
		headers,
	});
}

export function getLatestResult(headers?: any): Promise<MPTResultType> {
	return get(`${API_URL}/api-gateway/mpt/v1/latest-result`, { headers });
}

export function getOtherCohorts(
	id: string,
	headers?: any
): Promise<OtherCohortsType> {
	return get(`${API_URL}/api-gateway/mpt/v1/results/${id}/other-cohorts`, {
		headers,
	});
}

export function getGloablOtherCohorts(
	id: string,
	headers?: any
): Promise<OtherCohortsType> {
	return get(
		`${API_URL}/api-gateway/mpt/v1/global/results/${id}/other-cohorts`,
		{
			headers,
		}
	);
}

export const shareResult = (
	shareCard: {
		share_text: string;
		share_url: string;
	},
	isInApp: boolean,
	onSuccess: () => void,
	onFailure: () => void
) => {
	let shareData = {
		title: "SALT Money Personality Test",
		text: shareCard.share_text,
		url: shareCard.share_url,
	};

	const onFailureHandler = () => {
		onFailure();
	};

	if (isInApp) {
		sendToApp({ action: "share", data: shareData });
	} else {
		if (navigator.share)
			navigator
				.share(shareData)
				.then((s) => {})
				.catch((e) => {
					captureException(e);
				});
		else {
			copyToClipboard({
				text: shareCard.share_url,
				onSuccess,
				onFailure: onFailureHandler,
			});
		}
	}
};

export const getRemainingTime = (
	total: number,
	currentIndex: number,
	eachItemTimeInSeconds: number
): string => {
	const totalTimeInSeconds = total * eachItemTimeInSeconds;
	const remainingTimeInSeconds =
		totalTimeInSeconds - currentIndex * eachItemTimeInSeconds;

	const inMinutes = Math.floor(remainingTimeInSeconds / 60);
	const inSeconds = remainingTimeInSeconds % 60;

	return `${inMinutes}${inSeconds > 0 ? `:${inSeconds}` : ``} min`;
};

export function getCampaignVersionAndShareCode(
	s: string | string[] | undefined
) {
	return s
		? s.length > 1
			? s[0].length < 5
				? { campaignVersion: s[0], shareCode: s[1] }
				: { campaignVersion: s[1], shareCode: s[0] }
			: s.length == 1
			? s[0].length < 5
				? { campaignVersion: s[0] }
				: { shareCode: s[0] }
			: {}
		: {};
}

export const formatQuestions = (questions: QuestionOrderResponseType[]) => {
	const transformedQuestion = questions.map((q) =>
		transformQuestion(q.question)
	);

	const masterColors = [
		// color code order has to mentain
		"gold",
		"blue",
		"turquoise-blue",
		"orange",
		"lavender",
		"red",
	];
	const copySurvey = clone(transformedQuestion);
	const actualQuestions = copySurvey.filter((f) => f.type !== 0);

	actualQuestions.forEach((q, i) => {
		(q.idx = i + 1), (q.color = masterColors[i % masterColors.length]);
		q.completionPercentage = Math.round((i / actualQuestions.length) * 100);
		q.remainingTimeText = getRemainingTime(actualQuestions.length, i, 30);
	});

	return copySurvey;
};

/***
 * MPT state machine
 */

export type ContextType = {
	surveyId: number | null;
	campaignVersion: string;
	testInstanceIdentifier: string;

	submitSurveyIdentifier: string;
	routerQuery?: any;
	questions: Array<QuestionType>;
	shareCode: string;
	user: UserType | null;
};

type ErrorType = {
	data: ErrorResponseType;
};

type AnswerPayloadType = {
	id: number;
	answer: number;
};

interface EventType extends EventObject {
	data:
		| SurveyStartedEventResponseType
		| ErrorType
		| SubmitSurveyResponseType
		| AnswerPayloadType;
}

export const mptMachine = createMachine<ContextType, EventType>(
	{
		id: "mptMachine",
		context: {
			campaignVersion: "",
			surveyId: null,
			testInstanceIdentifier: "",
			submitSurveyIdentifier: "",
			questions: [],
			shareCode: "",
			user: null,
		},
		initial: "INTRO",
		states: {
			INTRO: {
				initial: "IDLE",
				entry: () =>
					trackEvent(AppEvent.screenView, {
						product: "mpt",
						label: "mpt_start",
						screen: "mpt_questions",
					}),
				states: {
					IDLE: {
						on: {
							START: {
								actions: () =>
									trackEvent(AppEvent.tap, {
										product: "mpt",
										label: "mpt_start_test",
										screen: "mpt_questions",
									}),
								target: "STARTING_SURVEY",
							},
						},
					},
					STARTING_SURVEY: {
						invoke: {
							src: "startSurvey",
							onDone: {
								actions: assign((_: ContextType, event: EventType) => ({
									testInstanceIdentifier: (
										event.data as SurveyStartedEventResponseType
									).test_instance_identifier,
								})),
								target: "#mptMachine.TAKE_SURVEY",
							},
							onError: {
								actions: (_: any, event: EventType) =>
									captureException(
										new Error(
											(event.data as ErrorType)?.data?.error?.message ||
												"Error in starting survey"
										)
									),
								target: "#mptMachine.TAKE_SURVEY",
							},
						},
					},
				},
			},
			TAKE_SURVEY: {
				initial: "IDLE",
				states: {
					IDLE: {
						on: {
							UPDATE_ANSWER: [
								{
									cond: (_: ContextType, event: EventType) => {
										return (
											_.questions.filter((f) => f.type !== 0).pop()?.id ===
											(event.data as AnswerPayloadType).id
										);
									},
									description: "If this is the last question to answer",
									actions: ["updateAnswer", "raiseAnswerEvent"],
									target: "SUBMITTING",
								},
								{
									actions: ["updateAnswer", "raiseAnswerEvent"],
								},
							],
							SUBMIT: "SUBMITTING",
						},
					},
					SUBMITTING: {
						invoke: {
							src: "submitSurveyAnswer",
							onDone: {
								actions: assign((_: ContextType, event: EventType) => ({
									submitSurveyIdentifier: (
										event.data as SubmitSurveyResponseType
									).identifier,
								})),
								target: "#mptMachine.RESULT",
							},
							onError: {
								actions: "setError",
								target: "",
							},
						},
					},
				},
			},
			RESULT: {
				type: "final",
				description: "Route to result page",
				entry: "redirectToResultPage",
			},
		},
	},
	{
		actions: {
			updateAnswer: assign((ctx: ContextType, event: EventType) => {
				const answer = event.data as AnswerPayloadType;
				const question = ctx.questions.find((f) => f.id === answer.id);
				if (question) {
					question.answer = `${answer.answer!}`;
				}
				return ctx;
			}),
			raiseAnswerEvent: (ctx: ContextType, event: EventType) => {
				// This event will raise when any answer selected for question.
				const answer = event.data as AnswerPayloadType;
				const question = ctx.questions.find((f) => f.id === answer.id);
				trackEvent(AppEvent.tap, {
					product: "mpt",
					label: "mpt_submit_answer",
					screen: "mpt_questions",
					name: question?.text,
					value:
						question?.answer && question?.choices
							? question.choices[parseInt(question.answer) - 1]?.text
							: undefined,
				});
			},
		},
		services: {
			startSurvey: (ctx: ContextType) =>
				publishSurveyStartedEvent({
					survey_id: ctx.surveyId!,
					survey_type: 1,
					version_identifier: ctx.campaignVersion,
				}),
			submitSurveyAnswer: (ctx: ContextType) =>
				submitSurvey(ctx.surveyId!, {
					metadata: {
						user_data: (ctx.user
							? {
									age: ctx.user.age,
									email: ctx.user.email,
									name: ctx.user.full_name,
									phone_number: ctx.user.phone_number,
									pronouns: ctx.user.pronouns,
							  }
							: {}) as UserMetaDataType,
						shared_code: ctx.shareCode,
						version_identifier: ctx.campaignVersion,
					},
					survey_id: ctx.surveyId!,
					survey_type: 1,
					test_instance_identifier: ctx.testInstanceIdentifier,
					responses: ctx.questions.map<QuestionAnswerType>((q) => ({
						question_id: String(q.id),
						answer: q.answer ? Number(q.answer) : -1,
						question_type: String(q.type),
					})),
				}),
		},
	}
);
